<template>
  <div class="bdt" :class="{ 'bdt--fixed': fixedLayout, 'bdt--resizing': resizing }" :style="rootVars">
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="options"
      :show-select="showSelect"
      :item-key="itemKey"
      :mobile-breakpoint="0"
      hide-default-footer
      class="bdt-table"
      :style="stickyVars"
    >
      <!-- Resize grip, one per column. It lives inside the header slot rather
           than being injected into the <th> by hand because that is the only
           hook v-data-table gives into a header cell — Vuetify still appends
           its own sort icon beside it. Clicks are swallowed so grabbing the
           grip never also sorts the column. -->
      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
        <span :key="`label-${h.value}`" class="bdt-th-inner">{{ header.text }}</span>
        <span
          :key="`grip-${h.value}`"
          class="bdt-th-grip"
          title="Drag to resize"
          @mousedown.stop.prevent="startResize($event, header)"
          @click.stop
        ></span>
      </template>

      <template v-for="h in headers" v-slot:[`item.${h.value}`]="{ item }">
        <BreadDataTableCell
          :key="h.value"
          :row="item"
          :header="h"
          :table-data="items"
          @open-menu="onOpenMenu"
        />
      </template>

      <template v-slot:no-data>
        <div class="bdt-empty">{{ error ? errorText : "No Record Found" }}</div>
      </template>
    </v-data-table>

    <!-- Footer is hand-built: v-data-footer has no numbered page buttons. -->
    <div class="bdt-footer">
      <div class="bdt-footer__counter">{{ counterText }}</div>
      <div class="bdt-footer__right">
        <span class="bdt-footer__label">Page Size</span>
        <v-select
          v-model="options.itemsPerPage"
          :items="paginationSizeSelector"
          dense
          solo
          flat
          hide-details
          class="bdt-pagesize"
          :menu-props="{ contentClass: 'bdt-menu', offsetY: true, top: true }"
        ></v-select>
        <button class="bdt-pg" :disabled="options.page <= 1" @click="goTo(1)">First</button>
        <button class="bdt-pg" :disabled="options.page <= 1" @click="goTo(options.page - 1)">Prev</button>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="bdt-pg bdt-pg--num"
          :class="{ 'bdt-pg--active': p === options.page }"
          @click="goTo(p)"
        >{{ p }}</button>
        <button class="bdt-pg" :disabled="options.page >= totalPages" @click="goTo(options.page + 1)">Next</button>
        <button class="bdt-pg" :disabled="options.page >= totalPages" @click="goTo(totalPages)">Last</button>
      </div>
    </div>

    <!-- One menu for the whole table, not one per row. -->
    <v-menu
      v-model="menuOpen"
      :position-x="menuX"
      :position-y="menuY"
      absolute
      offset-y
      content-class="bdt-menu"
    >
      <v-list dense>
        <v-list-item v-for="(mi, i) in menuItems" :key="i" link @click="runMenuItem(mi)">
          <v-list-item-title>{{ mi.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import axios from "axios";
import store from "@/store";
import BreadDataTableCell from "./breadDataTable/BreadDataTableCell.vue";
import { buildUrl, buildBrowseParams } from "./breadDataTable/serializeQuery";
import { toColumnKeys } from "./breadDataTable/columnCondition";
import {
  adaptColumns,
  toSortParams,
  fromSavedSort,
} from "./breadDataTable/columnAdapter";
import { exportRows } from "./breadDataTable/exportTable";

/** Narrower than this and a column stops being readable at all. */
const MIN_COLUMN_WIDTH = 60;
/** Fallback for a column that enters fixed mode without a measurable width. */
const DEFAULT_COLUMN_WIDTH = 150;
/** The select column is pinned to this by table-tokens.css (`width: 48px !important`). */
const SELECT_COLUMN_WIDTH = 48;

export default {
  name: "BreadDataTable",
  components: { BreadDataTableCell },
  props: {
    apiUrl: { type: String, default: "" },
    columns: { type: Array, default: () => [] },
    paginationSize: { type: Number, default: 10 },
    paginationSizeSelector: { type: Array, default: () => [10, 20, 30, 40, 50] },
    modelKey: { type: String, default: "" },
    columnSelectionDisabledCount: { type: Number, default: 0 },
    deferInitialLoad: { type: Boolean, default: false },
    // Accepted for drop-in compatibility with BreadTabulator; intentionally unused.
    layout: { type: String, default: "" },
    autoColumns: { type: Boolean, default: false },
    responsiveLayout: { type: Boolean, default: true },
  },
  data() {
    return {
      items: [],
      selected: [],
      totalItems: 0,
      totalPages: 1,
      loading: false,
      error: null,
      currentUrl: this.apiUrl,
      filters: [],
      extraParams: {},
      visibleKeys: null, // null = use the model's default visible set
      /**
       * `{ columnKey: px }`. EMPTY means "no one has resized anything" and the
       * table stays in its natural auto layout; non-empty flips it to the fixed
       * layout that makes dragging actually work (see `fixedLayout`).
       */
      columnWidths: {},
      resizing: false,
      options: {
        page: 1,
        itemsPerPage: this.paginationSize,
        sortBy: [],
        sortDesc: [],
      },
      menuOpen: false,
      menuX: 0,
      menuY: 0,
      menuItems: [],
    };
  },
  computed: {
    adapted() {
      return adaptColumns(this.columns, {
        visibleKeys: this.visibleKeys,
        widths: this.columnWidths,
      });
    },
    headers() {
      return this.adapted.headers;
    },
    showSelect() {
      return this.adapted.showSelect;
    },
    /**
     * v-data-table identifies a row by `item-key`, which defaults to `id`.
     * These browse endpoints key their records by uuid and return no `id` at
     * all, so every row resolved to the SAME key (`undefined`) and Vuetify
     * filed them all under one entry in its selection map — ticking one
     * checkbox ticked the whole page. `rownum` is stamped onto every row in
     * fetch() as `(page - 1) * size + i + 1`, so it is present on every row and
     * unique across the whole result set, not just the current page.
     */
    itemKey() {
      return "rownum";
    },
    /**
     * The sort the API is asked for, as `[{field, dir}]` — Tabulator's wire
     * shape, which the backend already speaks. v-data-table only reports the
     * user's intent (`sortBy`/`sortDesc`); because `server-items-length` is
     * bound it never reorders the rows itself, so sending this is what makes
     * sorting happen at all.
     */
    sortParams() {
      return toSortParams(this.options.sortBy, this.options.sortDesc, this.headers);
    },
    /**
     * Everything that must trigger a network request — and nothing else.
     * Column visibility is deliberately excluded, so showing/hiding a column
     * never costs a round trip. Because this is ONE reactive value, applying a
     * saved view (which sets filters + sort + page size + page synchronously)
     * collapses into a single watcher flush and a single request. That is what
     * replaces BreadTabulator's hand-rolled request coalescer.
     */
    requestState() {
      return {
        url: this.currentUrl,
        page: this.options.page,
        size: this.options.itemsPerPage,
        sort: this.sortParams,
        filter: this.filters,
        extra: this.extraParams,
      };
    },
    /**
     * Watch keys, not the objects themselves.
     *
     * `:options.sync` makes v-data-table hand back a BRAND NEW options object
     * whenever its internals settle, so `this.options` is reassigned even when
     * nothing about it changed. A `deep: true` watcher on a computed that
     * returns a fresh object/array fires on every re-evaluation regardless of
     * value (Vue 2 re-runs the callback whenever the watched value `isObject`),
     * so those watchers fired on that identity churn. For `sortParams` that
     * meant "the sort changed" -> page reset to 1 immediately after the user
     * picked page 2, which is why paging appeared not to work at all.
     *
     * Serialising to a string makes the comparison by VALUE, so the handlers
     * run only on real changes. Do not turn these back into deep watchers.
     */
    sortKey() {
      return JSON.stringify(this.sortParams);
    },
    requestKey() {
      return JSON.stringify(this.requestState);
    },
    counterText() {
      return `showing ${this.options.page} - ${this.totalPages} pages, ${this.totalItems} results found`;
    },
    errorText() {
      return "Failed to load records.";
    },
    pageWindow() {
      const total = this.totalPages || 1;
      const start = Math.max(1, Math.min(this.options.page - 1, total - 2));
      const out = [];
      for (let p = start; p < start + 3 && p <= total; p++) out.push(p);
      return out;
    },
    /**
     * Column resizing only works under `table-layout: fixed`.
     *
     * In the browser's auto table layout a column can never be narrower than
     * its widest cell's content, and these cells are `white-space: nowrap` — so
     * dragging a column NARROWER did nothing at all, no matter what width was
     * written onto the <th>. Fixed layout takes the width from the header cell
     * verbatim, which is what makes both directions work.
     *
     * The cost of fixed layout is that a column with no declared width gets an
     * equal share of the table instead of sizing to its content, and only 4 of
     * a typical model's ~29 columns declare one. So it is switched on ONLY once
     * a width exists, and whoever turns it on first (a drag, or a saved view
     * being applied) measures every other column beforehand and freezes it at
     * the width it already had. The table therefore never visibly reflows at
     * the moment resizing begins.
     */
    fixedLayout() {
      return Object.keys(this.columnWidths).length > 0;
    },
    /**
     * Under fixed layout the table needs an explicit width or the browser has
     * nothing to lay the columns out against. `max(...)` keeps it filling the
     * card when the columns do not add up to the full width, and lets it grow
     * past the card — into the wrapper's horizontal scroll — when they do.
     */
    tableWidth() {
      if (!this.fixedLayout) return null;
      let sum = this.showSelect ? SELECT_COLUMN_WIDTH : 0;
      this.headers.forEach((h) => {
        sum += h.width || DEFAULT_COLUMN_WIDTH;
      });
      return sum;
    },
    /** Custom properties inherit, so this sits on the wrapper, not the table. */
    rootVars() {
      return this.fixedLayout ? { "--bdt-table-width": `${this.tableWidth}px` } : {};
    },
    /** Per-column sticky offsets, exposed as CSS custom properties. */
    stickyVars() {
      const vars = {};
      this.headers.forEach((h) => {
        if (h.sticky) vars[`--bdt-sticky-${h.stickyIndex}`] = `${h.stickyLeft}px`;
      });
      return vars;
    },
    /**
     * DEPRECATED compat shim. Some pages reach past the wrapper into
     * `$refs.tabulatorDataTable.tabulator.*`. Only these four methods are used
     * anywhere in the codebase; this exists so a page migrates with a tag
     * rename, and is deleted once those call sites are rewritten. Do not grow it.
     */
    tabulator() {
      const vm = this;
      return {
        download(type, filename, opts, scope) {
          vm.exportData(type, scope, Object.assign({ filename }, opts || {}));
        },
        deselectRow() {
          vm.clearSelection();
        },
        setPage(page) {
          vm.setCurrentPagNumber(page);
        },
        setColumns() {
          /* columns are reactive here; nothing to do */
        },
        getSelectedData() {
          return vm.selected;
        },
      };
    },
  },
  watch: {
    requestKey() {
      // Any state change means the page is driving the table, which
      // implicitly releases a deferred initial load.
      this._ready = true;
      this.scheduleFetch();
    },
    "options.itemsPerPage": function (v) {
      this.$emit("getCurrentPageSize", v);
      // A different page size renumbers every page, so the page the user was on
      // no longer points at the same rows — and on a larger size it can fall
      // past the last page, which makes the API return an empty result.
      if (this._skipPageReset) return;
      this.options.page = 1;
    },
    "options.page": function (v) {
      this.$store.commit("assignDeveloperData", { paginationPageNo: v });
      this.$emit("getCurrentPageNumber", v);
    },
    // Watch the resolved sort, not `sortBy` alone: toggling asc -> desc leaves
    // the column list unchanged, and the saved view stores the direction.
    sortKey() {
      this.$emit("getCurrentSort", this.sortParams);

      // Re-sorting reorders the whole result set, so the page the user was
      // on no longer means anything — Tabulator reset to page 1 on every
      // remote sort and filter, and getFilterCondition() already matches.
      // Applying a saved view is the exception: it restores its own page
      // right after setting the sort, and that page must win.
      if (this._skipPageReset) return;
      this.options.page = 1;
    },
    selected(v) {
      this.$emit("getSelectedRows", v);
    },
    apiUrl(v) {
      this.currentUrl = v;
    },
    /**
     * A column turned on from the column picker AFTER resizing started has no
     * width, and under fixed layout an unsized column is handed an equal share
     * of the table rather than sized to its content. Give it the model's width
     * (or a sane default) so it lands at a believable size. Not emitted: the
     * user has not chosen this width, so it is not worth persisting into a view.
     */
    headers() {
      if (!this.fixedLayout) return;
      const next = Object.assign({}, this.columnWidths);
      let changed = false;
      this.headers.forEach((h) => {
        if (!next[h.value]) {
          next[h.value] = h.width || DEFAULT_COLUMN_WIDTH;
          changed = true;
        }
      });
      if (changed) this.columnWidths = next;
    },
  },
  created() {
    this._ready = !this.deferInitialLoad;
    this._seq = 0;
    this._pendingTick = false;
    this._skipPageReset = false;
    this._loaded = false;
    this._resize = null;
    this._swallowClick = null;
    this._clickGuardTimer = null;
  },
  mounted() {
    if (this._ready) this.scheduleFetch();
  },
  beforeDestroy() {
    if (this._source) this._source.cancel("component destroyed");
    // The drag listeners live on `window`, so a component torn down mid-drag
    // (route change, tab switch) would leave them behind pointing at a dead vm.
    this.stopResizeListeners();
    this.clearClickGuard();
  },
  methods: {
    // ── data ──────────────────────────────────────────────────────────
    scheduleFetch() {
      if (this._pendingTick) return;
      this._pendingTick = true;
      this.$nextTick(() => {
        this._pendingTick = false;
        this.fetch();
      });
    },
    fetch() {
      const state = this.requestState;
      if (!state.url) return;

      if (this._source) this._source.cancel("superseded");
      this._source = axios.CancelToken.source();

      const seq = ++this._seq;
      this.loading = true;
      this.error = null;

      const params = buildBrowseParams({
        page: state.page,
        size: state.size,
        sort: state.sort,
        filter: state.filter,
        extra: state.extra,
      });

      axios
        .get(buildUrl(state.url, params), {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + store.state.auth.data.token,
          },
          cancelToken: this._source.token,
        })
        .then((res) => {
          if (seq !== this._seq) return; // a newer request already landed
          const body = res.data || {};
          const rows = body.data || [];
          const info = body.info || {};
          const start = (state.page - 1) * state.size;
          rows.forEach((row, i) => {
            row.rownum = start + i + 1;
          });
          this.items = rows;
          // A new result set replaces every row, so selections made against the
          // old one no longer have a visible checkbox backing them — they would
          // silently ride along into "Refund Client"/export payloads. Tabulator
          // dropped its selection on each remote load; keep that behaviour.
          if (this.selected.length) this.selected = [];
          this.totalItems = info.totalItems || 0;
          this.totalPages = info.totalPages || 1;
          this.loading = false;
          this._loaded = true;
          // A page restored from a saved view (or from the store) is applied
          // before the page count is known, so it can land past the end. Now
          // that the count is real, snap back — one extra request, only ever in
          // the out-of-range case, and it cannot recur because the snapped page
          // is by definition in range.
          if (state.page > this.totalPages) this.options.page = this.totalPages;
          this.$emit("response", rows);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          if (seq !== this._seq) return;
          this.loading = false;
          this.error = err;
          this.items = [];
          this.$emit("getDataLoadErrorCode", err);
        });
    },
    goTo(page) {
      const n = Math.max(1, parseInt(page, 10) || 1);
      // Clamp only once a response has said how many pages exist. A page
      // restored from a saved view is applied BEFORE the first load, while
      // `totalPages` is still its placeholder 1 — clamping then would collapse
      // every restored page to 1. Out-of-range pages are corrected in fetch()
      // instead, once the real count is known.
      const p = this._loaded ? Math.min(n, this.totalPages || 1) : n;
      if (p !== this.options.page) this.options.page = p;
    },
    onOpenMenu({ event, items, cell }) {
      this.menuOpen = false;
      this.menuItems = items || [];
      // Tabulator passed (event, cell) to every menu action, and statically
      // declared menus (contextMenu arrays) still rely on that second argument
      // to reach their row — so keep the originating cell alongside the items.
      // Deliberately NOT in data(): a DOM event and a cell facade have nothing
      // the template renders, and observing them would be pure overhead.
      this._menuEvent = event;
      this._menuCell = cell || null;
      this.menuX = event.clientX;
      this.menuY = event.clientY;
      this.$nextTick(() => {
        this.menuOpen = true;
      });
    },
    runMenuItem(mi) {
      this.menuOpen = false;
      if (typeof mi.action === "function") mi.action(this._menuEvent, this._menuCell);
    },

    // ── public API (mirrors BreadTabulator so pages swap by tag rename) ──
    /**
     * "The page is configuring me" — release any deferred initial load and make
     * sure a request happens even when the applied state EQUALS the current
     * state. Applying the default "all" view sets filters to `[]` and page to 1,
     * which are already the defaults, so the requestKey watcher alone would
     * never fire and a deferInitialLoad table would sit loading forever.
     * Coalesced like every other fetch, so applying a whole saved view (url +
     * filters + sort + page size + page) still costs exactly one request.
     */
    release() {
      this._ready = true;
      this.scheduleFetch();
    },
    getFilterCondition(conditions) {
      this.filters = Array.isArray(conditions) ? conditions : [];
      this.options.page = 1;
      this.release();
    },
    setAdvancedFilterConditions(params) {
      this.extraParams = Object.assign({}, this.extraParams, {
        advanceFilterParam: JSON.stringify(params),
      });
      this.release();
    },
    applyColumnSelection(selectedColumns) {
      // Tolerates the legacy object shape as well as field strings.
      this.visibleKeys = toColumnKeys(selectedColumns);
    },
    callApiUrl(url) {
      this.currentUrl = url;
      this.release();
    },
    /**
     * Apply a saved-view mutation without the "reset to page 1" reflex.
     *
     * Restoring a view sets sort AND page size AND then the page it wants, all
     * synchronously; the watchers only run afterwards, on one flush. If they
     * reset the page, the view's own page is lost.
     *
     * The clear is queued AFTER `apply()` on purpose. Vue queues the watcher
     * flush on $nextTick from the FIRST reactive mutation, so registering the
     * clear beforehand would put it in front of the flush and disarm the guard
     * before any watcher had run. Clearing on $nextTick rather than letting the
     * first watcher consume the flag means both the sort and the page-size
     * watcher see it, and a restore whose values already match (no watcher
     * fires at all) does not leave the flag armed for the next real sort.
     */
    holdPage(apply) {
      this._skipPageReset = true;
      apply();
      this.$nextTick(() => {
        this._skipPageReset = false;
      });
    },
    callSavedPageSize(size) {
      const n = parseInt(size, 10);
      this.holdPage(() => {
        if (n && n !== this.options.itemsPerPage) this.options.itemsPerPage = n;
      });
      this.release();
    },
    callSavedColumnSort(savedSort) {
      const next = fromSavedSort(savedSort, this.headers);
      // This is a restore, not a user re-sort: keep the page the view restores.
      this.holdPage(() => {
        this.options.sortBy = next.sortBy;
        this.options.sortDesc = next.sortDesc;
      });
      this.release();
    },
    // ── column resizing ──────────────────────────────────────
    /**
     * The width every visible column is CURRENTLY rendered at, read off the DOM.
     *
     * This is what makes the switch into fixed layout invisible: the columns the
     * user never touched keep the size the auto layout had just given them. Must
     * therefore be called while the table is still in auto layout — measuring
     * after the switch reads back the equal-share widths fixed layout invented.
     */
    measureColumnWidths() {
      const out = {};
      const ths = this.$el
        ? this.$el.querySelectorAll(".bdt-table .v-data-table-header th.bdt-th")
        : [];
      // `.bdt-th` is on every adapted column and on none of Vuetify's own
      // (the select-all header), so this list lines up with `headers` by index.
      this.headers.forEach((h, i) => {
        const th = ths[i];
        out[h.value] =
          (th && Math.round(th.getBoundingClientRect().width)) ||
          h.width ||
          DEFAULT_COLUMN_WIDTH;
      });
      return out;
    },
    startResize(event, header) {
      const th = event.target.closest && event.target.closest("th");
      if (!th) return;

      // Freeze the whole row of columns BEFORE anything switches to fixed
      // layout. `froze` records that THIS drag is what turned fixed layout on,
      // so a click that never moves can put it back.
      let froze = false;
      if (!this.fixedLayout) {
        this.columnWidths = this.measureColumnWidths();
        froze = true;
      }

      this._resize = {
        key: header.value,
        th,
        startX: event.clientX,
        // Read before the re-render; the element is patched in place, not
        // replaced, so `th` stays valid for the whole drag.
        startWidth: Math.round(th.getBoundingClientRect().width),
        width: null,
        froze,
      };
      this.resizing = true;
      window.addEventListener("mousemove", this.onResizeMove);
      window.addEventListener("mouseup", this.onResizeEnd);
    },
    /**
     * Written straight to the DOM, deliberately. Committing to `columnWidths`
     * on every mousemove would re-render all ~29 columns x 50 rows through the
     * cell formatters at pointer rate. Under fixed layout the header cell alone
     * decides the column width, so touching that one element is enough for the
     * drag to look live; the reactive commit happens once, on release.
     */
    onResizeMove(event) {
      const r = this._resize;
      if (!r) return;
      const width = Math.max(
        MIN_COLUMN_WIDTH,
        Math.round(r.startWidth + (event.clientX - r.startX))
      );
      r.th.style.width = `${width}px`;
      r.th.style.minWidth = `${width}px`;
      r.th.style.maxWidth = `${width}px`;
      // Keep the table's own width in step, so widening the last column extends
      // the scrollable area instead of squeezing its neighbours.
      if (this.$el) {
        this.$el.style.setProperty(
          "--bdt-table-width",
          `${this.tableWidth - r.startWidth + width}px`
        );
      }
      r.width = width;
    },
    onResizeEnd() {
      const r = this._resize;
      this.stopResizeListeners();
      this._resize = null;
      this.resizing = false;
      if (!r) return;

      // A click on the grip that never moved is not a resize: undo the freeze
      // it armed, so idly clicking a header divider cannot switch the whole
      // table into fixed layout and mark the view dirty.
      if (r.width === null) {
        if (r.froze) this.columnWidths = {};
        return;
      }

      this.suppressNextClick();
      this.$set(this.columnWidths, r.key, r.width);
      this.emitColumnWidths();
    },
    stopResizeListeners() {
      window.removeEventListener("mousemove", this.onResizeMove);
      window.removeEventListener("mouseup", this.onResizeEnd);
    },
    /**
     * Swallow the `click` the browser synthesizes at the end of the drag.
     *
     * A click is dispatched on the nearest common ancestor of the mousedown and
     * mouseup targets — and for a drag that stays inside the column it started
     * in (any narrowing drag, and any small widening one) that ancestor is the
     * <th>. Vuetify's sort handler is bound to exactly that element, so every
     * resize also re-sorted the column being resized. `@click.stop` on the grip
     * cannot help: the click is not dispatched on the grip.
     *
     * Capture phase, so it lands before Vuetify's listener rather than after,
     * and torn down either by the click itself or by a timeout — the click may
     * never arrive (mouseup outside the window), and a listener left armed
     * would eat the user's next real click. The timeout cannot fire first:
     * mouseup and its click are dispatched together, before any task queued
     * from within the mouseup handler.
     */
    suppressNextClick() {
      this.clearClickGuard();
      this._swallowClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.clearClickGuard();
      };
      window.addEventListener("click", this._swallowClick, true);
      this._clickGuardTimer = setTimeout(this.clearClickGuard, 0);
    },
    clearClickGuard() {
      if (this._swallowClick) {
        window.removeEventListener("click", this._swallowClick, true);
        this._swallowClick = null;
      }
      if (this._clickGuardTimer) {
        clearTimeout(this._clickGuardTimer);
        this._clickGuardTimer = null;
      }
    },
    /**
     * Publish the WHOLE width map, never just the column that moved.
     *
     * The page accumulates these into `allResizeColumns`, and the view strip's
     * Save button writes that array to `CrmView.manageColumnCustomWidth`
     * verbatim — so whatever is not published here is dropped from the view on
     * the next save. Only ever emitting the dragged column meant that nudging
     * one column of a view that already had stored widths saved a view holding
     * that column alone, and the rest of the layout was lost.
     *
     * Hidden columns are included too (they live in `columnWidths` but not in
     * `headers`): a column the current view does not show still keeps the width
     * it was given, instead of being reset by every save.
     */
    emitColumnWidths() {
      Object.keys(this.columnWidths).forEach((key) => this.emitColumnWidth(key));
    },
    /**
     * Shaped as a Tabulator column so the pages' existing
     * `getCurrentColumnWidth(col)` handlers — which call `getField()` /
     * `getWidth()` and accumulate into `allResizeColumns` for
     * `CrmView.manageColumnCustomWidth` — keep working untouched.
     *
     * The field reported is the ADAPTER's key, not `def.field`, because that is
     * what `callSavedHeaderWidth` looks widths up by on the way back in. It is
     * also the only identity the width-less columns ("#", "Actions") have.
     */
    emitColumnWidth(key) {
      const width = this.columnWidths[key];
      if (!width) return;
      this.$emit("getCurrentColumnWidth", {
        getField: () => key,
        getWidth: () => width,
      });
    },
    /**
     * Stored component-locally on purpose. BreadTabulator wrote widths onto the
     * shared module-singleton column array, which leaked them across pages and
     * into persisted saved views.
     *
     * An empty list is the reset used by the "All" and "Trashed" tabs: it drops
     * the table back to its natural auto layout. A non-empty one is a saved
     * view, and it is completed with the natural width of every column the view
     * has no opinion about — measured from the auto layout, which is why an
     * already-fixed table is released back to auto for a tick first.
     *
     * A restored view publishes its widths straight back out, which is what
     * makes Save round-trip: the page's `allResizeColumns` starts empty on every
     * load, so pressing Save after changing only a filter used to overwrite the
     * view's stored widths with `[]` and silently wipe them. Deliberately NOT
     * done for the empty case — a view with no stored widths must not acquire a
     * frozen layout just because someone opened it and saved.
     */
    callSavedHeaderWidth(widths) {
      const saved = (Array.isArray(widths) ? widths : []).filter(
        (w) => w && w.field && parseInt(w.width, 10)
      );
      if (!saved.length) {
        this.columnWidths = {};
        return;
      }
      const apply = () => {
        const next = this.measureColumnWidths();
        saved.forEach((w) => {
          next[w.field] = parseInt(w.width, 10);
        });
        this.columnWidths = next;
        this.emitColumnWidths();
      };
      if (this.fixedLayout) {
        this.columnWidths = {};
        this.$nextTick(apply);
      } else {
        apply();
      }
    },
    setCurrentPagNumber(page) {
      this.goTo(parseInt(page, 10) || 1);
      this.release();
    },
    setCurrentPagSize(size) {
      this.callSavedPageSize(size);
    },
    updateTableRowData(rowId, name, value) {
      const row = this.items.find((r) => r.id === rowId);
      if (row) this.$set(row, name, value);
    },
    clearTableData() {
      this.items = [];
      this.totalItems = 0;
      this.totalPages = 1;
    },
    /**
     * Re-request the current page. Needed because a fetch is driven by request
     * state CHANGING — after a mutating action the page wants the same query
     * re-run, which no watcher would ever see. Coalesced like any other fetch,
     * so calling it alongside a state change still costs one request.
     */
    refresh() {
      this.release();
    },
    /** Same thing, named for what a deferInitialLoad page means by it. */
    releaseDeferredLoad() {
      this.refresh();
    },
    updateUrlParams(page, pageSize) {
      const url = new URL(window.location);
      url.searchParams.set("page", page);
      url.searchParams.set("pageSize", pageSize);
      window.history.pushState({}, "", url);
    },
    exportData(type, scope, opts) {
      const rows =
        scope === "selected" && this.selected.length ? this.selected : this.items;
      exportRows(type, rows, this.headers, opts);
    },
    clearSelection() {
      this.selected = [];
    },
  },
};
</script>

<style scoped>
/* Row height is a MINIMUM, never fixed — the Stages cell stacks multiple alert
   pills and must be free to grow. `.bdt-cell` carries the min-height instead. */
.bdt-table >>> tbody td {
  height: auto;
}
/* Cell content never wraps — otherwise the narrow "#" column pushes its
   duplicate/flag badges onto a second line instead of sitting beside the index.
   This does not affect the Stages cell, whose formatter stacks pills with
   flex-direction: column. */
.bdt-table >>> tbody td,
.bdt-table >>> th {
  white-space: nowrap;
}
/* Sticky columns need an OPAQUE background or scrolled content bleeds through. */
.bdt-table >>> .bdt-sticky {
  position: sticky;
  z-index: 2;
  background: var(--bdt-surface);
}
.bdt-table >>> th.bdt-sticky {
  z-index: 4;
  background: var(--bdt-surface-alt);
}
.bdt-table >>> .bdt-sticky--0 { left: var(--bdt-sticky-0, 0px); }
.bdt-table >>> .bdt-sticky--1 { left: var(--bdt-sticky-1, 0px); }
.bdt-table >>> .bdt-sticky--2 { left: var(--bdt-sticky-2, 0px); }
.bdt-table >>> .bdt-sticky--3 { left: var(--bdt-sticky-3, 0px); }
.bdt-table >>> tbody tr:hover .bdt-sticky { background: var(--bdt-row-hover); }


/* ── column resizing ──────────────────────────────────────────────────────
   The grip is absolutely positioned against its own <th>, so every header
   needs to be a containing block. `:not(.bdt-sticky)` because a frozen header
   is already `position: sticky` — which is equally a containing block — and
   this rule would otherwise out-order it and unfreeze the column. */
.bdt-table >>> .v-data-table-header th:not(.bdt-sticky) {
  position: relative;
}
.bdt-th-inner {
  /* inline-block, not block: the sort icon is a SIBLING inside the <th>, and a
     block-level label would push it onto its own line. */
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.bdt-th-grip {
  position: absolute;
  top: 0;
  right: 0;
  /* Kept wholly INSIDE the header cell: a resized table clips its cells
     (`overflow: hidden` below), and anything hanging over the column border
     would lose both its paint and its hit area to that clip. */
  width: 10px;
  height: 100%;
  cursor: col-resize;
  z-index: 5;
}
.bdt-th-grip::after {
  content: "";
  position: absolute;
  top: 20%;
  bottom: 20%;
  right: 1px;
  width: 2px;
  border-radius: 1px;
  background: var(--bdt-accent, #1976d2);
  opacity: 0;
  transition: opacity 0.15s ease;
}
.bdt-th-grip:hover::after {
  opacity: 1;
}
/* While dragging, the pointer must not turn into a text caret or a sort cursor
   just because it wandered off the 10px grip. */
.bdt--resizing {
  cursor: col-resize;
  user-select: none;
}
.bdt--resizing >>> * {
  cursor: col-resize !important;
  user-select: none !important;
}

/* Fixed layout is what makes a column narrowable at all — see `fixedLayout`.
   `max()` so the table still fills the card when the columns do not add up to
   it, and overflows into the wrapper's own horizontal scroll when they do. */
.bdt--fixed >>> .v-data-table__wrapper > table {
  table-layout: fixed;
  width: max(var(--bdt-table-width, 100%), 100%);
}
/* Widths are written onto the header cell; border-box keeps the number the user
   dragged to and the number the column renders at the same one. */
.bdt--fixed >>> th,
.bdt--fixed >>> tbody td {
  box-sizing: border-box;
  overflow: hidden;
}
/* .bdt-cell is a flex row, so its children are flex items and will not shrink
   past their content without min-width: 0 — which is what leaves room for the
   ellipsis instead of letting the text spill under the next column. */
.bdt--fixed >>> .bdt-cell > * {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bdt-empty {
  padding: 28px 0;
  color: var(--bdt-text-faint);
  font-weight: 600;
}
.bdt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
}
.bdt-footer__counter,
.bdt-footer__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--bdt-text-muted);
}
.bdt-footer__right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
/* Page-size select matched to the pagination pills. */
.bdt-pagesize >>> .v-input__slot {
  background: var(--bdt-surface) !important;
  border: 1px solid var(--tbl-border) !important;
  border-radius: var(--tbl-radius) !important;
  min-height: 32px !important;
  box-shadow: none !important;
  padding: 0 10px !important;
}
.bdt-pagesize >>> .v-input__slot:hover {
  border-color: var(--tbl-border-strong) !important;
}
.bdt-pagesize >>> .v-select__selection {
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  color: var(--bdt-text) !important;
}
.bdt-pagesize >>> .v-input__append-inner {
  margin-top: 4px !important;
}
.bdt-pagesize >>> .v-icon {
  font-size: 18px !important;
  color: var(--bdt-text-muted) !important;
}
.bdt-pagesize {
  max-width: 92px;
}
/* Every pagination control is a bordered pill; the current page fills solid. */
.bdt-pg {
  min-width: 40px;
  height: 32px;
  padding: 0 12px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--bdt-text-muted);
  background: var(--bdt-surface);
  border: 1px solid var(--tbl-border);
  border-radius: var(--tbl-radius);
  cursor: pointer;
  transition: all 0.15s ease;
}
.bdt-pg:hover:not([disabled]) {
  background: var(--bdt-row-hover);
  border-color: var(--tbl-border-strong);
  color: var(--bdt-text);
}
.bdt-pg[disabled] {
  color: var(--bdt-text-faint);
  cursor: default;
}
.bdt-pg--num {
  min-width: 34px;
  padding: 0 8px;
}
.bdt-pg--active,
.bdt-pg--active:hover {
  background: var(--bdt-accent) !important;
  border-color: var(--bdt-accent) !important;
  color: var(--bdt-accent-fg) !important;
}
</style>
